import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Store, UtensilsCrossed, Warehouse, Briefcase, Building } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { COMMERCIAL_SEGMENTS, commercialFeedQuery } from '@/data/audiences';

/** Segment key -> icon. Keys mirror COMMERCIAL_SEGMENTS in src/data/audiences.ts. */
const SEGMENT_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  'property-managers': Building2,
  retail: Store,
  restaurants: UtensilsCrossed,
  warehouses: Warehouse,
  office: Briefcase,
  multifamily: Building,
};

/**
 * COMMERCIAL PROPERTY SECTION.
 *
 * Every chip is a REAL query into the existing marketplace
 * (`/jobs?type=commercial`), using the `project_type` filter the browse page
 * already implements — so no segment link can land on an invented page, and a
 * segment with no live work shows the honest empty state rather than a fake
 * count. No property/job counts are printed here because there is no per-
 * segment column in the database to count.
 */
const CommercialSegments: React.FC = () => {
  const { t, path } = useI18n();

  return (
    <section className="bg-brand-navy py-14 sm:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-brand-blue/20 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-blue-200 ring-1 ring-blue-400/30">
              <Building2 className="h-3.5 w-3.5" aria-hidden="true" /> {t('aud.commercialEyebrow')}
            </span>
            <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              {t('aud.commercialSectionTitle')}
            </h2>
            <p className="mt-3 text-base leading-relaxed text-slate-300">{t('aud.commercialSectionSub')}</p>
          </div>
          <Link
            to={`${path('/jobs')}${commercialFeedQuery()}`}
            className="inline-flex items-center gap-1.5 rounded-xl border border-white/25 bg-white/10 px-5 py-3 text-sm font-bold text-white backdrop-blur transition-all duration-200 hover:-translate-y-0.5 hover:bg-white/20"
          >
            {t('aud.viewCommercial')} <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>

        <ul className="mt-8 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-6">
          {COMMERCIAL_SEGMENTS.map((seg) => {
            const Icon = SEGMENT_ICONS[seg.key] || Building2;
            return (
              <li key={seg.key}>
                <Link
                  to={`${path('/jobs')}${commercialFeedQuery(seg)}`}
                  className="group flex h-full flex-col items-start gap-2.5 rounded-2xl border border-white/10 bg-white/[0.07] p-4 transition-all duration-200 hover:-translate-y-1 hover:border-white/25 hover:bg-white/[0.12]"
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-blue/20 text-blue-200 ring-1 ring-blue-400/25 transition-transform duration-200 group-hover:scale-110">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="text-sm font-bold leading-snug text-white">{t(seg.labelKey)}</span>
                </Link>
              </li>
            );
          })}
        </ul>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Link
            to={`${path('/register')}?role=homeowner&type=commercial`}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-orange px-6 py-4 text-base font-bold text-white shadow-glow transition-all duration-200 hover:-translate-y-0.5 hover:bg-orange-600"
          >
            {t('aud.commercialCta')} <ArrowRight className="h-5 w-5" aria-hidden="true" />
          </Link>
          <Link
            to={path('/commercial')}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/25 bg-transparent px-6 py-4 text-base font-bold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-white/10"
          >
            {t('aud.commercialLearn')}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CommercialSegments;
